import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AvatarFit,
  ChangePasswordInput,
  MeResponse,
  MeUpdateInput,
  UserCreateInput,
  UserPublic,
  UserResetPasswordInput,
  UserUpdateInput,
} from '@underline/shared';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('users').json<UserPublic[]>(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  return useMutation({
    mutationFn: (input: MeUpdateInput) =>
      api.patch('auth/me', { json: input }).json<MeResponse>(),
    onSuccess: (data) => {
      setUser(data.user);
      qc.setQueryData(['me'], data.user);
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (input: ChangePasswordInput) => {
      await api.post('auth/change-password', { json: input });
    },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UserCreateInput) =>
      api.post('users', { json: input }).json<UserPublic>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UserUpdateInput }) =>
      api.patch(`users/${id}`, { json: input }).json<UserPublic>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: async ({ id, input }: { id: number; input: UserResetPasswordInput }) => {
      await api.post(`users/${id}/reset-password`, { json: input });
    },
  });
}

export function useUploadMyAvatar() {
  const qc = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  const me = useAuthStore((s) => s.user);
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post('auth/me/avatar', { body: fd }).json<{ avatar_url: string }>();
    },
    onSuccess: (data) => {
      if (me) setUser({ ...me, avatar_url: data.avatar_url });
      void qc.invalidateQueries({ queryKey: ['me'] });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUploadUserAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post(`users/${id}/avatar`, { body: fd }).json<{ avatar_url: string }>();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUploadUserAvatarOriginal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post(`users/${id}/avatar`, { body: fd }).json<{
        avatar_url: string;
        img_w: number;
        img_h: number;
      }>();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useSaveUserAvatarFit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, fit }: { id: number; fit: AvatarFit }) =>
      api.patch(`users/${id}/avatar-fit`, { json: fit }).json<{ avatar_url: string }>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUploadMyAvatarOriginal() {
  const qc = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  const me = useAuthStore((s) => s.user);
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post('auth/me/avatar', { body: fd }).json<{
        avatar_url: string;
        img_w: number;
        img_h: number;
      }>();
    },
    onSuccess: (data) => {
      if (me) setUser({ ...me, avatar_url: data.avatar_url });
      void qc.invalidateQueries({ queryKey: ['me'] });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useSaveMyAvatarFit() {
  const qc = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  const me = useAuthStore((s) => s.user);
  return useMutation({
    mutationFn: async (fit: AvatarFit) =>
      api.patch('auth/me/avatar-fit', { json: fit }).json<{ avatar_url: string }>(),
    onSuccess: (data) => {
      if (me) setUser({ ...me, avatar_url: data.avatar_url });
      void qc.invalidateQueries({ queryKey: ['me'] });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`users/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
